import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { usePageTitle } from "@/hooks/usePageTitle";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight, FileText, Tag } from "lucide-react";

export default function ContentHubPage() {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  usePageTitle("Content Hub");

  useEffect(() => {
    Promise.all([
      api.get("/content/content_hub"),
      api.get("/content/content_categories"),
    ]).then(([a, c]) => {
      setArticles(a.data.filter(x => x.is_published !== false));
      setCategories(c.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const filtered = activeCategory === "all"
    ? articles
    : articles.filter(a => a.category === activeCategory);

  return (
    <div data-testid="content-hub-page">
      <div className="bg-jci-black py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight">Content Hub</h1>
          <p className="text-white/50 mt-3 text-base">Articles, insights, and stories from JCI Bangladesh</p>
        </div>
        <div className="h-1 bg-gradient-to-r from-jci-blue via-jci-teal to-jci-navy mt-8" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl py-16">
        {/* Category Filter */}
        <div className="flex flex-wrap gap-3 mb-10" data-testid="category-filters">
          <Button
            variant={activeCategory === "all" ? "default" : "outline"}
            onClick={() => setActiveCategory("all")}
            className={activeCategory === "all" ? "bg-jci-blue hover:bg-jci-navy text-white" : ""}
            data-testid="filter-all"
          >
            All
          </Button>
          {categories.map(cat => (
            <Button
              key={cat.id}
              variant={activeCategory === cat.name ? "default" : "outline"}
              onClick={() => setActiveCategory(cat.name)}
              className={activeCategory === cat.name ? "bg-jci-blue hover:bg-jci-navy text-white" : ""}
              data-testid={`filter-${cat.slug}`}
            >
              <Tag className="w-3.5 h-3.5 mr-1.5" /> {cat.name}
            </Button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1,2,3].map(i => <Skeleton key={i} className="h-80 rounded-2xl" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <FileText className="w-16 h-16 text-slate-200 mx-auto mb-4" />
            <p className="text-slate-400">No articles available yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="content-hub-grid">
            {filtered.map((item, i) => (
              <Link to={`/content-hub/${item.slug}`} key={item.id} data-testid={`hub-card-${i}`}>
                <Card className="border-0 shadow-md overflow-hidden group cursor-pointer h-full hover:shadow-lg transition-shadow">
                  <div className="overflow-hidden h-52">
                    {item.image_url ? (
                      <img src={item.image_url} alt={item.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    ) : (
                      <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                        <FileText className="w-12 h-12 text-slate-300" />
                      </div>
                    )}
                  </div>
                  <CardContent className="p-6">
                    {item.category && (
                      <span className="text-xs font-semibold text-jci-teal bg-jci-teal/10 px-2 py-1 rounded-full mb-3 inline-block">{item.category}</span>
                    )}
                    <h3 className="font-bold text-jci-black text-base mb-2 line-clamp-2 group-hover:text-jci-blue transition-colors">{item.title}</h3>
                    <p className="text-sm text-slate-500 line-clamp-3">{item.excerpt}</p>
                    <span className="inline-flex items-center gap-1 text-xs font-semibold text-jci-blue mt-4 group-hover:gap-2 transition-all">
                      Read More <ArrowRight className="w-3 h-3" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
